import type { MaybeRef, UnwrapRef } from 'vue';
import { isReactive, isRef, onMounted, reactive, ref, unref, watch } from 'vue';
import type { ColumnType } from 'ant-design-vue/lib/table';
import { filterNullAndEmptyKeys } from '../function';
import { getApiData } from '../../api/api-model';

export interface PageInfo {
  current: number;
  pageSize: number;
  total: number;

  [key: string]: any;
}

export interface ReponseData<T> {
  data: T[];
  success?: boolean;
  total?: number;

  [key: string]: any;
}

export type RequestParams =
  | {
      pageSize: number;
      current: number;
      [key: string]: any;
    }
  | undefined;

export interface UseFetchDataAction<T extends ReponseData<any>> {
  stripe: (record: any, index: number) => string | undefined;
  cancel: () => void;
  reload: (auto_refresh?: boolean) => Promise<void>;
  resetPageIndex: () => void;
  reset: () => void;
  context: Context<T>;
  setPageInfo: (pageInfo: Partial<PageInfo>) => void;
}

export interface Context<T extends ReponseData<any>> {
  current: number;
  pageSize: number;
  dataSource: T['data'];
  loading: boolean;
  total: number;
  requestParams?: MaybeRef<{
    [key: string]: any;
  }>;

  [key: string]: any;
}

export const defaultContext: Context<any> = {
  stripe: false,
  loading: false,
  current: 1,
  pageSize: 50,
  total: 0,
  dataSource: [],
};

// 如果请求数据中，没有分页，仅单列表数据，可以使用该方法进行包装，免去重复写请求方法
export const wrap = (req: () => Promise<any[]>): (() => Promise<ReponseData<any>>) => {
  return () =>
    req().then(res => {
      const data = res;
      return {
        data,
        total: data.length,
        success: true,
      };
    });
};

export const useFetchData = <T extends ReponseData<any>>(
  getData: MaybeRef<string>,
  context: MaybeRef<{
    stripe?: boolean;
    current?: number;
    pageSize?: number;
    dataSource?: T['data'];
    loading?: boolean;
    requestParams?: MaybeRef<{
      [key: string]: any;
    }>;
    [key: string]: any;
  }> = reactive({ ...defaultContext }),
  options?: {
    current?: number;
    pageSize?: number;
    onLoad?: (dataSource: T['data']) => void;
    onRequestError?: (e: Error) => void;
    pagination?: boolean;
  },
  processData?: (raw_data_list: T[]) => T[],
  processColumn?: (data_list: T[], raw_data_list: T[]) => ColumnType[],
  replace_column?: (tmp_columns: ColumnType[]) => void,
  callback?: () => void,
): UseFetchDataAction<T> => {
  const state = reactive({ ...defaultContext } as Context<T>);
  const mergeContext = isReactive(context) || isRef(context) ? context : ref(context);
  watch(
    mergeContext,
    () => {
      Object.assign(state, unref(context));
    },
    { immediate: true },
  );

  const fetchList = async (auto_refresh: boolean = false) => {
    // 请求中禁止重复请求
    if (state.loading || state.init_request === false) {
      state.init_request = true;
      return;
    }
    const url = unref(getData);
    if (!url) {
      return;
    }
    state.loading = true;
    const { pageSize = 50, current = 1 } = state;
    try {
      const params: RequestParams =
        options?.pagination !== false
          ? {
              current,
              pageSize,
              ...state.requestParams,
              // ...filterNoValidValue(unref(mergeContext).requestParams),
            }
          : undefined;
      if (!auto_refresh) {
        state.dataSource = [] as UnwrapRef<T['data']>;
      }
      await getApiData(params, url.replace('__tree', ''), url.includes('__tree'))
        .then(res => {
          const data = res.data;
          const column_data = res.columns;
          const success = res.success as boolean;
          const dataTotal = res.total as number;
          // const { data = [], success, total: dataTotal = 0 } = await
          if (success !== false) {
            const processed_data = processData ? processData(data) : data;
            if (processColumn && replace_column && column_data) {
              replace_column(processColumn(data, column_data));
            }
            state.dataSource = [].concat(processed_data) as UnwrapRef<T['data']>;
            state.total = dataTotal;
            state.current = res.current as number;
            state.tableHead = res.table_head ? res.table_head : null;
            state.sum_data = res.sum_data ?? null;
            if (options?.onLoad) {
              options?.onLoad(processed_data);
            }
          }
        })
        .finally(() => {
          state.loading = false;
        });
    } catch (e: any) {
      state.loading = false;
      // 如果没有传递这个方法的话，需要把错误抛出去，以免吞掉错误
      if (options?.onRequestError === undefined) {
        throw new Error(e);
      } else {
        options.onRequestError(e);
      }
    }
  };

  const cancel = () => {};

  const reset = () => {};

  const reload = (auto_refresh: boolean = false): Promise<void> => {
    return new Promise(resolve => {
      resolve(fetchList(auto_refresh));
    });
  };

  const setPageInfo = (pageInfo: Partial<PageInfo>) => {
    pageInfo.current && (state.current = pageInfo.current);
    pageInfo.pageSize && (state.pageSize = pageInfo.pageSize);
    state.requestParams = filterNullAndEmptyKeys(pageInfo);
  };

  const resetPageIndex = (): void => {
    state.current = 1;
  };
  // watch(
  //   [() => state.current, () => state.pageSize, () => ({ ...state.requestParams })],
  //   (nextValue, preValue) => {
  //     if (!isEqual(nextValue, preValue)) {
  //       fetchList().catch(e => {
  //         throw new Error(e);
  //       }).finally(() => {
  //         if (callback) {
  //           callback();
  //         }
  //       });
  //     }
  //   },
  //   { immediate: true, deep: true },
  // );

  const stripe = (_: any, index: number) =>
    index % 2 === 1 && state.stripe && 'ant-pro-table-row-striped';

  onMounted(() => {
    // if (state.init_request === false) {
    //   return;
    // }
    // fetchList()
    //   .catch(e => {
    //     throw new Error(e);
    //   })
    //   .finally(() => {
    //     if (callback) {
    //       callback();
    //     }
    //   });
  });

  return {
    stripe,
    cancel,
    reset,
    reload,
    resetPageIndex,
    setPageInfo: info => {
      setPageInfo({
        current: state.current,
        pageSize: state.pageSize,
        ...info,
      });
    },
    context: state,
  };
};
